import React, { useRef, useEffect, useCallback, useState } from "react";
import { StyleSheet, View } from "react-native";
import { RouteProp, useFocusEffect, useRoute, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import CustomText from "../components/common/general/CustomText/CustomText";
import Spline from "@splinetool/react-spline";
import Loader from "../components/common/status/Loader/Loader";
import "@livekit/components-styles";
import { LiveKitRoom, RoomAudioRenderer, VoiceAssistantControlBar } from "@livekit/components-react";
import { usePostHog } from "posthog-js/react";
import BlankLayout from "../layouts/BlankLayout";
import AssessmentMenuContainer from "../containers/navigation/AssessmentMenuContainer/AssessmentMenuContainer";
import ErrorLoader from "../components/common/status/ErrorLoader/ErrorLoader";
import AssessmentBeginModal from "../containers/assessment/AssessmentBeginModal.js";
import {
  useGetAssessmentQuery,
  useGetCourseByIdQuery,
  useGetRunningAssessmentQuery,
} from "../graphql/generated/graphql";
import AssessmentCompleteModal from "../containers/assessment/AssessmentCompleteModal";
import ExitBeforeCompletedModal from "../containers/assessment/ExitBeforeCompletedModal";
import CursorLeavesWindowModal from "../containers/assessment/CursorLeavesWindowModal";
import AssessmentInProgressModal from "../containers/assessment/AssessmentInProgressModal";
import { Application } from "@splinetool/runtime";
import AssessmentMessageHandler from "../containers/assessment/AssessmentMessageHandler";
import AssessmentErrorModal from "../containers/assessment/AssessmentErrorModal";
import AssessmentOrbController from "../containers/assessment/AssessmentOrbController";
import SimpleVoiceAssistant from "../containers/assessment/AssessmentSimpleVoiceAssistant";
import { fetchLiveKitToken, LiveKitActionMessageType } from "../containers/assessment/AssessmentUtils";
import { sendLiveKitMessage } from "../containers/assessment/AssessmentUtils";
import AssessmentCompletedModal from "../containers/assessment/AssessmentCompletedModal";
import { AssessmentNoiseFilter } from "../containers/assessment/AssessmentNoiseFilter";

export const DEFAULT_ASSESSMENT_TIME_IN_MINUTES = 25;
export const DEFAULT_QUESTIONS_COUNT = 10;

function CustomCourseAssessmentStudentView() {
  const posthog = usePostHog();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "Custom Course Assessment">>();
  const { assessment_id: assessmentId, course_id: courseId, integration_id: integrationId } = route.params;

  const [beginModalVisible, setBeginModalVisible] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [anotherAssessmentInProgressModalVisible, setAnotherAssessmentInProgressModalVisible] = useState(false);
  const [cursorLeavesWindowModalVisible, setCursorLeavesWindowModalVisible] = useState(false);
  const [liveKitToken, setLiveKitToken] = useState("");
  const [orbLoading, setOrbLoading] = useState(true);
  const [agentWarmingUp, setAgentWarmingUp] = useState(false);
  const [displayTime, setDisplayTime] = useState(`${DEFAULT_ASSESSMENT_TIME_IN_MINUTES}:00`);
  const [isTerminatingAssessment, setIsTerminatingAssessment] = useState(false);
  const [isCleanupInProgress, setIsCleanupInProgress] = useState(false);
  const [assessmentErrorModalVisible, setAssessmentErrorModalVisible] = useState(false);
  const [assessmentAlreadyCompletedModalVisible, setAssessmentAlreadyCompletedModalVisible] = useState(false);
  const orb = useRef(null);
  const [roomInstance, setRoomInstance] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);

  const handleRoomReady = useCallback((room) => {
    setRoomInstance(room);
  }, []);

  const { refetch } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const wsURL = process.env.LIVEKIT_URL;

  const {
    data: assessment,
    loading: assessmentLoading,
    error: assessmentError,
  } = useGetAssessmentQuery({
    variables: {
      assessmentId,
    },
    skip: !assessmentId,
  });

  const {
    data: runningAssessment,
    loading: runningAssessmentLoading,
    refetch: refetchRunningAssessment,
  } = useGetRunningAssessmentQuery({
    skip: !assessmentId,
  });

  useFocusEffect(
    useCallback(() => {
      if (assessmentId) {
        refetchRunningAssessment();
      }
    }, [assessmentId, refetchRunningAssessment])
  );

  const activeAssessmentId = roomInstance?.roomInfo?.name.split("assessment-")[1]?.split("-user-")[0];

  const assessmentIsRunning =
    displayTime !== "00:00" &&
    displayTime !== `${DEFAULT_ASSESSMENT_TIME_IN_MINUTES}:00` &&
    !!activeAssessmentId &&
    activeAssessmentId === assessmentId;

  const assessmentIsEnabled = assessment?.getAssessment?.data[0]?.isEnabled;
  const assessmentProperties = assessment?.getAssessment?.data[0]?.userHasAssessmentConnection?.edges?.[0]?.properties;
  //assessment is marked as running after it started on server, but only as a background process, so it is possible that it is already running, but not yet marked as running on the frontend, so use this with caution
  const assessmentNotStartedIdDatabase = !assessmentProperties?.isCompleted && !assessmentProperties?.isRunning;
  const assessmentIsRunningInDatabase =
    assessment?.getAssessment?.data[0]?.userHasAssessmentConnection?.edges?.[0]?.properties?.isRunning;
  const assessmentIsCompletedInDatabase =
    assessment?.getAssessment?.data[0]?.userHasAssessmentConnection?.edges?.[0]?.properties?.isCompleted;

  const userAlreadyHasAnotherRunningAssessment =
    runningAssessment?.getRunningAssessment?.assessmentId &&
    runningAssessment?.getRunningAssessment?.assessmentId !== assessmentId;

  const handleLiveKitTokenError = () => {
    setAgentWarmingUp(false);
    setBeginModalVisible(false);
    setAssessmentErrorModalVisible(true);
    console.error("Error fetching LiveKit token");
  };

  const handleLiveKitTokenFetched = async (token: string) => {
    setLiveKitToken(token);
  };

  useEffect(() => {
    posthog?.capture("Assessment Page Visited");
  }, []);

  useFocusEffect(
    useCallback(() => {
      const initializePageState = async () => {
        const assessmentIsActive = !!liveKitToken;

        if (assessmentIsCompletedInDatabase && !assessmentAlreadyCompletedModalVisible) {
          setAssessmentAlreadyCompletedModalVisible(true);
          return;
        }

        if (userAlreadyHasAnotherRunningAssessment) {
          setAnotherAssessmentInProgressModalVisible(true);
          return;
        }

        if (assessmentIsActive) {
          return;
        }

        const showBeginModal = assessmentNotStartedIdDatabase && !activeAssessmentId && !liveKitToken;

        if (showBeginModal) {
          setBeginModalVisible(true);
          return;
        }
        if (assessmentIsRunningInDatabase) {
          setAgentWarmingUp(true);
          await fetchLiveKitToken(assessmentId, handleLiveKitTokenFetched, handleLiveKitTokenError);
          return;
        }
      };

      if (!assessmentLoading && !runningAssessmentLoading) {
        initializePageState();
      }
    }, [
      assessmentId,
      assessmentProperties,
      assessmentLoading,
      liveKitToken,
      activeAssessmentId,
      assessmentNotStartedIdDatabase,
      assessmentIsRunningInDatabase,
      runningAssessmentLoading,
    ])
  );

  const handleVisibilityChange = useCallback(() => {
    if (!assessmentIsRunning) {
      return;
    }
    if (document.hidden) {
      setCursorLeavesWindowModalVisible(true);
    }
  }, [assessmentIsRunning]);

  const handleMouseLeave = useCallback(
    (event) => {
      if (!assessmentIsRunning) {
        return;
      }
      const { clientX, clientY } = event;

      const leftEdge = clientX <= 0;
      const rightEdge = clientX >= window.innerWidth;
      const topEdge = clientY <= 0;
      const bottomEdge = clientY >= window.innerHeight;

      if (leftEdge || rightEdge || topEdge || bottomEdge) {
        setCursorLeavesWindowModalVisible(true);
      }
    },
    [assessmentIsRunning]
  );

  useFocusEffect(
    useCallback(() => {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.removeEventListener("mouseleave", handleMouseLeave);
      };
    }, [assessmentIsRunning])
  );

  if (assessmentLoading) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </BlankLayout>
    );
  }
  if (assessmentError || !assessmentIsEnabled) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          {assessmentError ? (
            <ErrorLoader message="Something went wrong! Try reloading the page." />
          ) : (
            <ErrorLoader message="This assessment is not enabled." />
          )}
        </View>
      </BlankLayout>
    );
  }

  const handleExitPage = () => {
    navigation.navigate("Custom Course Student", {
      course_id: courseId,
      integration_id: integrationId,
    });
  };

  const handleBeginAssessment = async () => {
    if (assessmentIsRunning || isCleanupInProgress) {
      console.error("Assessment is already running or cleanup is in progress");
      return;
    }
    setAgentWarmingUp(true);
    await fetchLiveKitToken(assessmentId, handleLiveKitTokenFetched, handleLiveKitTokenError);
    setBeginModalVisible(false);
  };

  const handleCancelAssessmentBegin = () => {
    setBeginModalVisible(false);
    handleExitPage();
  };

  const handleAssessmentComplete = () => {
    setCompleteModalVisible(true);
    setDisplayTime("00:00");
  };

  const handleConfirmCompleteModal = () => {
    setCompleteModalVisible(false);
    refetch();
    setDisplayTime(`${DEFAULT_ASSESSMENT_TIME_IN_MINUTES}:00`);
    setLiveKitToken("");
    setRoomInstance(null);
    handleExitPage();
  };

  const handleTerminateAssessment = async () => {
    if (!roomInstance || !assessmentId) {
      console.error("Cannot terminate assessment - room or assessmentId unavailable");
      return;
    }
    setIsTerminatingAssessment(true);
    setExitModalVisible(false);
    await sendLiveKitMessage(LiveKitActionMessageType.TerminateAssessment, roomInstance, assessmentId);
    await refetch();
    setLiveKitToken("");
    setRoomInstance(null);
    setDisplayTime(`${DEFAULT_ASSESSMENT_TIME_IN_MINUTES}:00`);
    setIsTerminatingAssessment(false);
    handleExitPage();
  };

  const handleAnotherAssessmentInProgressModalClick = () => {
    setAnotherAssessmentInProgressModalVisible(false);
    handleExitPage();
  };

  const handleExitButtonPress = () => {
    if (assessmentIsRunning) {
      setExitModalVisible(true);
    } else {
      handleExitPage();
    }
  };

  const onLoad = (spline: Application) => {
    setOrbLoading(false);
    const obj = spline.findObjectById("02f9e61e-081c-484d-8258-c53281906a3f");
    orb.current = obj;
  };

  const handleAssessmentErrorModalClick = () => {
    setAssessmentErrorModalVisible(false);
    handleExitPage();
  };

  const handleInitializationErrorInLiveKitAgent = () => {
    setDisplayTime(`${DEFAULT_ASSESSMENT_TIME_IN_MINUTES}:00`);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("mouseleave", handleMouseLeave);
    setAssessmentErrorModalVisible(true);
  };

  const handleTimerUpdated = (time: string) => {
    if (agentWarmingUp) {
      setAgentWarmingUp(false);
    }
    setDisplayTime(time);
  };

  const handleAssessmentCompletedModalClick = () => {
    setAssessmentAlreadyCompletedModalVisible(false);
    handleExitPage();
  };

  const handleQuestionIndexUpdate = (index: number) => {
    setQuestionIndex(index);
  };

  const showAssessmentAsRunningOrLoading =
    !assessmentErrorModalVisible &&
    !assessmentAlreadyCompletedModalVisible &&
    !anotherAssessmentInProgressModalVisible &&
    !beginModalVisible &&
    !completeModalVisible;

  return (
    <BlankLayout>
      <View style={styles.pageContainer}>
        <AssessmentMenuContainer
          onComplete={handleAssessmentComplete}
          handleExitPress={handleExitButtonPress}
          defaultTime={DEFAULT_ASSESSMENT_TIME_IN_MINUTES}
          displayTime={displayTime}
          activeAssessmentId={activeAssessmentId}
          setDisplayTime={setDisplayTime}
          isTerminatingAssessment={isTerminatingAssessment}
        />
        <View style={styles.contentContainer}>
          {agentWarmingUp && <CustomText text="Agent is warming up... This may take a few seconds." size="xl" />}
          {isCleanupInProgress && <CustomText text="Cleaning up assessment data... Please wait." size="xl" />}
          {liveKitToken ? (
            <LiveKitRoom
              style={styles.liveKitContainer}
              token={liveKitToken}
              serverUrl={wsURL}
              connect={true}
              audio={true}>
              <AssessmentMessageHandler
                onComplete={handleAssessmentComplete}
                onTimerUpdated={handleTimerUpdated}
                onTimerStarted={handleTimerUpdated}
                assessmentId={assessmentId}
                setIsCleanupInProgress={setIsCleanupInProgress}
                onRoomReady={handleRoomReady}
                assessmentIsRunning={assessmentIsRunning}
                onInitializationErrorCallback={handleInitializationErrorInLiveKitAgent}
                onQuestionIndexUpdate={handleQuestionIndexUpdate}
              />
              <AssessmentNoiseFilter />
              {showAssessmentAsRunningOrLoading && (
                <View style={styles.orbContainer}>
                  {orbLoading && <Loader />}
                  <Spline scene="https://prod.spline.design/eGVArbV9JzcVNsir/scene.splinecode" onLoad={onLoad} />
                  <AssessmentOrbController orb={orb} />
                </View>
              )}
              <SimpleVoiceAssistant />
              {questionIndex > 0 && (
                <CustomText
                  text="current_question_index"
                  size="xl"
                  interpolation={{ questionIndex, totalQuestions: DEFAULT_QUESTIONS_COUNT }}
                />
              )}
              <VoiceAssistantControlBar controls={{ leave: false }} />
              <RoomAudioRenderer />
            </LiveKitRoom>
          ) : (
            <>{showAssessmentAsRunningOrLoading && <Loader />}</>
          )}
        </View>
      </View>
      <AssessmentBeginModal
        visible={beginModalVisible}
        onCancel={handleCancelAssessmentBegin}
        onConfirm={handleBeginAssessment}
        defaultTime={DEFAULT_ASSESSMENT_TIME_IN_MINUTES}
        defaultQuestionsCount={DEFAULT_QUESTIONS_COUNT}
        isCleanupInProgress={isCleanupInProgress}
      />
      <AssessmentCompleteModal visible={completeModalVisible} onConfirm={handleConfirmCompleteModal} />
      <ExitBeforeCompletedModal
        visible={exitModalVisible}
        onConfirm={handleTerminateAssessment}
        onCancel={() => setExitModalVisible(false)}
      />
      <CursorLeavesWindowModal
        visible={cursorLeavesWindowModalVisible}
        onConfirm={() => setCursorLeavesWindowModalVisible(false)}
      />
      <AssessmentInProgressModal
        visible={anotherAssessmentInProgressModalVisible}
        onConfirm={handleAnotherAssessmentInProgressModalClick}
      />
      <AssessmentErrorModal visible={assessmentErrorModalVisible} onConfirm={handleAssessmentErrorModalClick} />
      <AssessmentCompletedModal
        visible={assessmentAlreadyCompletedModalVisible}
        onConfirm={handleAssessmentCompletedModalClick}
      />
    </BlankLayout>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "center",
    padding: 20,
    backgroundColor: "#E1E5FA",
  },
  liveKitContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  orbContainer: {
    justifyContent: "center",
    alignItems: "center",
    height: 450,
  },
});

export default CustomCourseAssessmentStudentView;
